/**
 * Connected Exports Tab - Exports Page
 *
 * Shows connection health and outbound sync configurations.
 * Reuses ConnectionSyncStatusCard from Data page but with outbound configs.
 */

import { Space, Button, Tag, Typography, Alert, Card } from 'antd'
import { PlusOutlined, ApiOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { ConnectionSyncStatusCard } from '@/components/connections/ConnectionSyncStatusCard'
import { exportService } from '@/services/exportService'
import type { Connection } from '@/types/connection.types'

const { Title, Text } = Typography

// Mock connection data - in real implementation, fetch from connection service
const MOCK_CONNECTIONS: Connection[] = []

export function ConnectedExportsTab() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch outbound configs
  const { data: outboundConfigsResponse } = useQuery({
    queryKey: ['export-configs'],
    queryFn: () => exportService.getOutboundConfigs(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const outboundConfigs = outboundConfigsResponse?.data || []

  // Trigger manual sync mutation
  const triggerSync = useMutation({
    mutationFn: (connectionUid: string) => exportService.triggerSync(connectionUid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['export-history'] })
      queryClient.invalidateQueries({ queryKey: ['export-configs'] })
      message.success('Export sync started')
    },
    onError: () => {
      message.error('Failed to start export sync')
    },
  })

  // Navigate to centralized Connections page
  const handleAddConnection = () => {
    navigate('/connections')
  }

  const handleSyncNow = (connectionUid: string) => {
    triggerSync.mutate(connectionUid)
  }

  const handleViewLogs = (connectionUid: string) => {
    console.log('View logs for connection:', connectionUid)
    // TODO: Navigate to logs page when implemented
  }

  // const handleConfigure = (connectionUid: string) => {
  //   console.log('Configure export for connection:', connectionUid)
  //   // TODO: Open configuration modal when implemented
  // }

  // Count connections by health status
  const healthyCount = MOCK_CONNECTIONS.filter(
    (c) => c.credentialStatus === 'valid' && c.state === 'active'
  ).length
  const warningCount = MOCK_CONNECTIONS.filter(
    (c) => c.credentialStatus === 'expired' || c.state === 'paused'
  ).length

  // Build outbound config map
  const outboundConfigMap: Record<string, any> = {}
  outboundConfigs.forEach((config) => {
    outboundConfigMap[config.connectionUid] = {
      configured: true,
      destination: config.destination,
      lastExportAt: config.lastSyncAt,
    }
  })

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header with summary stats and Add Connection button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space size="large">
          <Title level={5} style={{ margin: 0 }}>
            Active Connections
          </Title>
          <Space>
            {healthyCount > 0 && <Tag color="success">{healthyCount} Healthy</Tag>}
            {warningCount > 0 && <Tag color="warning">{warningCount} Needs Attention</Tag>}
          </Space>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddConnection}>
          Add Connection
        </Button>
      </div>

      {/* Connection Status Cards */}
      {MOCK_CONNECTIONS.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <ApiOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Title level={4} type="secondary">
              No Connections Yet
            </Title>
            <Text type="secondary">
              Connect to Square, QuickBooks, or Shopify to automatically export your prices.
            </Text>
            <br />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddConnection}
              style={{ marginTop: '16px' }}
            >
              Add Your First Connection
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {MOCK_CONNECTIONS.map((connection) => (
            <ConnectionSyncStatusCard
              key={connection.uid}
              connection={connection}
              outbound={outboundConfigMap[connection.uid]}
              onSyncNow={() => handleSyncNow(connection.uid)}
              onViewLogs={() => handleViewLogs(connection.uid)}
              onReviewDrafts={() => {
                /* No drafts on exports page */
              }}
            />
          ))}
        </>
      )}

      {/* Help text explaining outbound syncs */}
      <Alert
        message="About Export Syncs"
        description={
          <Space direction="vertical" size={4}>
            <Text>
              Export syncs push approved prices from Rocket Pricing to connected platforms
              (Square, QuickBooks, Shopify).
            </Text>
            <Text>
              Manage OAuth credentials, schedules, and advanced settings in{' '}
              <Button
                type="link"
                size="small"
                onClick={handleAddConnection}
                style={{ padding: 0 }}
              >
                Settings <ArrowRightOutlined /> Connections
              </Button>
            </Text>
          </Space>
        }
        type="info"
        showIcon
      />
    </Space>
  )
}
